/**
 * Brand DNA Page - Holo.ai-style brand voice analysis
 * Analyze website and view/edit brand characteristics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Icon } from '../components/icons';
import { format } from 'date-fns';

interface BrandDNAData {
  id: string;
  brand_id: string;
  website_url: string | null;
  website_scraped_at: number | null;
  guidelines_md: string | null;
  tone: string[];
  personality_traits: string[];
  writing_style: {
    sentence_length: string;
    formality: string;
    vocabulary: string;
  };
  common_phrases: string[];
  hashtag_style: string[];
  emoji_usage: {
    frequency: string;
    types: string[];
  };
  cta_patterns: string[];
  target_audience: {
    demographics: string[];
    interests: string[];
    pain_points: string[];
  };
  industry_keywords: string[];
  competitor_differentiation: string | null;
  preferred_content_types: string[];
  posting_themes: string[];
  analysis_confidence: number;
}

export function BrandDNA() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();
  const [brandDNA, setBrandDNA] = useState<BrandDNAData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [mdContent, setMdContent] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (selectedBrand) {
      fetchBrandDNA();
      setWebsiteUrl(selectedBrand.website_url || '');
    }
  }, [selectedBrand]);

  const fetchBrandDNA = async () => {
    if (!selectedBrand) return;

    setIsLoading(true);
    try {
      const response = await apiClient.getBrandDNA(selectedBrand.id);
      if (response.success) {
        setBrandDNA(response.data || null);
        if (response.data?.website_url) {
          setWebsiteUrl(response.data.website_url);
        }
      }
    } catch (error) {
      console.error('Failed to fetch Brand DNA:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copySummaryToClipboard = () => {
    if (!brandDNA) return;
    const summary = [
      `Tone: ${brandDNA.tone.join(', ')}`,
      `Personality: ${brandDNA.personality_traits.join(', ')}`,
      `CTA patterns: ${brandDNA.cta_patterns.join('; ')}`,
      `Common phrases: ${brandDNA.common_phrases.slice(0, 5).join(', ')}`,
      `Hashtag style: ${brandDNA.hashtag_style.join(', ')}`,
    ].join('\n');
    navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAnalyze = async () => {
    if (!selectedBrand || !websiteUrl) {
      setError('Please enter a website URL');
      return;
    }

    setIsAnalyzing(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiClient.analyzeBrandWebsite(selectedBrand.id, websiteUrl);

      if (response.success && response.data) {
        setSuccess(
          response.data.created
            ? 'Brand DNA created successfully!'
            : 'Brand DNA updated successfully!'
        );
        await fetchBrandDNA();
      } else {
        setError('Failed to analyze website');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to analyze website');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleImportMd = async () => {
    if (!selectedBrand || !mdContent.trim()) {
      setError('Please paste your brand guidelines content');
      return;
    }

    setIsImporting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/brand-dna/import-md', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          brand_id: selectedBrand.id,
          guidelines_md: mdContent,
        }),
      });

      const data = await response.json() as { success: boolean; message?: string; error?: string };

      if (response.ok && data.success) {
        setSuccess(data.message || 'Brand guidelines imported successfully!');
        setShowImportModal(false);
        setMdContent('');
        await fetchBrandDNA();
      } else {
        setError(data.error || 'Failed to import guidelines');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to import guidelines');
    } finally {
      setIsImporting(false);
    }
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-6">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Brand DNA</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Brand voice and guidelines for{' '}
            <span className="font-medium" style={{ color: selectedBrand.color_primary }}>
              {selectedBrand.name}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
          >
            <Icon name="file-text" size="sm" className="mr-2" />
            Import Guidelines
          </Button>
          {brandDNA && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Icon name="check" size="sm" className="text-green-500" />
              <span>{Math.round((brandDNA.analysis_confidence || 0) * 100)}%</span>
            </div>
          )}
          {brandDNA && (
            <Button
              variant="secondary"
              size="sm"
              onClick={copySummaryToClipboard}
            >
              <Icon name={copied ? 'check' : 'copy'} size="sm" className="mr-2" />
              {copied ? 'Copied' : 'Copy DNA summary'}
            </Button>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader
              title="Import Brand Guidelines"
              subtitle="Paste your brand guidelines markdown content"
              action={
                <button
                  onClick={() => setShowImportModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Icon name="x" size="md" />
                </button>
              }
            />
            <div className="flex-1 overflow-auto p-4">
              <textarea
                value={mdContent}
                onChange={(e) => setMdContent(e.target.value)}
                placeholder="Paste your brand guidelines markdown content here...

# Brand Name Guidelines

## Mission
...

## Tone of Voice
...

## Words to Use / Avoid
..."
                className="w-full h-80 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-2">
                This content will be used as context when generating AI content for your brand.
              </p>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImportMd}
                disabled={!mdContent.trim() || isImporting}
              >
                {isImporting ? (
                  <>
                    <Icon name="loader" size="sm" className="mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Icon name="save" size="sm" className="mr-2" />
                    Import Guidelines
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Website Analyzer */}
      <Card>
        <CardHeader
          title="Website Analysis"
          subtitle="Enter your website URL to extract brand voice and characteristics"
        />

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              placeholder="https://your-website.com"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              className="w-full"
            />
          </div>
          <Button
            onClick={handleAnalyze}
            isLoading={isAnalyzing}
            disabled={!websiteUrl}
            className="w-full sm:w-auto"
          >
            {brandDNA ? (
              <>
                <Icon name="refresh" size="sm" className="mr-2" />
                Re-analyze
              </>
            ) : (
              <>
                <Icon name="globe" size="sm" className="mr-2" />
                Analyze Website
              </>
            )}
          </Button>
        </div>

        {brandDNA?.website_scraped_at && (
          <p className="mt-3 text-sm text-gray-500">
            Last analyzed: {format(new Date(brandDNA.website_scraped_at), 'MMM d, yyyy h:mm a')}
          </p>
        )}
      </Card>

      {isLoading ? (
        <Card className="text-center py-16">
          <Icon name="sparkles" size="3xl" className="text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Loading Brand DNA...</p>
        </Card>
      ) : !brandDNA ? (
        <Card className="text-center py-16">
          <Icon name="file-text" size="3xl" className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No Brand Guidelines found</p>
          <p className="text-sm text-gray-400 mb-4">
            Import your brand guidelines to enable consistent AI content generation
          </p>
          <Button onClick={() => setShowImportModal(true)}>
            <Icon name="file-text" size="sm" className="mr-2" />
            Import Guidelines
          </Button>
        </Card>
      ) : (
        <>
          {/* Quick summary */}
          <Card>
            <CardHeader
              title="Brand DNA Summary"
              subtitle="Use this as context when prompting generators"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-800">
              <div>
                <p className="font-semibold text-gray-900 mb-1">Tone</p>
                <p className="text-gray-700">{brandDNA.tone.join(', ')}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Personality</p>
                <p className="text-gray-700">{brandDNA.personality_traits.join(', ')}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Common phrases</p>
                <p className="text-gray-700 line-clamp-2">{brandDNA.common_phrases.join(', ')}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">CTA patterns</p>
                <p className="text-gray-700 line-clamp-2">{brandDNA.cta_patterns.join('; ')}</p>
              </div>
            </div>
          </Card>

          {/* Brand Guidelines (MD Content) */}
          {brandDNA.guidelines_md && (
            <Card>
              <CardHeader
                title="Brand Guidelines"
                subtitle="Full brand voice and content guidelines"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowImportModal(true)}
                  >
                    <Icon name="edit" size="sm" className="mr-2" />
                    Update
                  </Button>
                }
              />
              <div className="prose prose-sm max-w-none">
                <div
                  className="whitespace-pre-wrap font-sans text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: brandDNA.guidelines_md
                      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-gray-900 mt-6 mb-3">$1</h1>')
                      .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold text-gray-800 mt-5 mb-2 pb-1 border-b">$1</h2>')
                      .replace(/^### (.+)$/gm, '<h3 class="text-lg font-medium text-gray-800 mt-4 mb-2">$1</h3>')
                      .replace(/^\*\*(.+?)\*\*/gm, '<strong class="font-semibold text-gray-900">$1</strong>')
                      .replace(/^\- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
                      .replace(/^✅ (.+)$/gm, '<div class="flex items-start gap-2 text-green-700 bg-green-50 p-2 rounded my-1"><span>✅</span><span>$1</span></div>')
                      .replace(/^\| (.+) \|$/gm, '<div class="bg-gray-50 p-2 rounded my-1 font-mono text-sm">$1</div>')
                      .replace(/^---$/gm, '<hr class="my-4 border-gray-200" />')
                      .replace(/\n\n/g, '</p><p class="mb-3">')
                  }}
                />
              </div>
            </Card>
          )}

          {/* Show analysis section if no MD content, or as additional info */}
          {!brandDNA.guidelines_md && (
            <>
          {/* Brand Voice */}
          <Card>
            <CardHeader title="Brand Voice" subtitle="Your brand's tone and personality" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="message" size="md" className="text-blue-500" />
                  <h4 className="font-medium text-gray-900">Tone</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brandDNA.tone.map(t => (
                    <span
                      key={t}
                      className="px-3 py-1.5 text-sm rounded-full bg-blue-100 text-blue-800"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="sparkles" size="md" className="text-purple-500" />
                  <h4 className="font-medium text-gray-900">Personality</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brandDNA.personality_traits.map(t => (
                    <span
                      key={t}
                      className="px-3 py-1.5 text-sm rounded-full bg-purple-100 text-purple-800"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Writing Style */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">Writing Style</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Sentence Length</p>
                  <p className="font-medium capitalize">
                    {brandDNA.writing_style?.sentence_length || 'Medium'}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Formality</p>
                  <p className="font-medium capitalize">
                    {brandDNA.writing_style?.formality || 'Casual'}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Vocabulary</p>
                  <p className="font-medium capitalize">
                    {brandDNA.writing_style?.vocabulary || 'Simple'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Content Patterns */}
          <Card>
            <CardHeader title="Content Patterns" subtitle="Common phrases and style elements" />

            <div className="space-y-6">
              {/* Common Phrases */}
              {brandDNA.common_phrases.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="sparkles" size="md" className="text-yellow-500" />
                    <h4 className="font-medium text-gray-900">Common Phrases</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brandDNA.common_phrases.map((phrase, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 text-sm rounded-lg bg-yellow-50 text-yellow-800 border border-yellow-200"
                      >
                        "{phrase}"
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Hashtag Style */}
              {brandDNA.hashtag_style.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="hash" size="md" className="text-blue-500" />
                    <h4 className="font-medium text-gray-900">Hashtag Style</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brandDNA.hashtag_style.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 text-sm rounded bg-gray-100 text-gray-700"
                      >
                        {tag.startsWith('#') ? tag : `#${tag}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Emoji Usage */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="smile" size="md" className="text-orange-500" />
                  <h4 className="font-medium text-gray-900">Emoji Usage</h4>
                </div>
                <p className="text-gray-700 capitalize">
                  Frequency: <span className="font-medium">{brandDNA.emoji_usage?.frequency || 'Moderate'}</span>
                </p>
                {brandDNA.emoji_usage?.types && brandDNA.emoji_usage.types.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {brandDNA.emoji_usage.types.map((type, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 text-xs rounded bg-orange-50 text-orange-700"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA Patterns */}
              {brandDNA.cta_patterns.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="trending-up" size="md" className="text-green-500" />
                    <h4 className="font-medium text-gray-900">Call-to-Action Patterns</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brandDNA.cta_patterns.map((cta, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 text-sm rounded-lg bg-green-50 text-green-800 border border-green-200"
                      >
                        {cta}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Target Audience */}
          <Card>
            <CardHeader title="Target Audience" subtitle="Who your content resonates with" />

            <div className="space-y-6">
              {/* Interests */}
              {brandDNA.target_audience?.interests && brandDNA.target_audience.interests.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="target" size="md" className="text-red-500" />
                    <h4 className="font-medium text-gray-900">Interests</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brandDNA.target_audience.interests.map((interest, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 text-sm rounded-full bg-red-50 text-red-700"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pain Points */}
              {brandDNA.target_audience?.pain_points && brandDNA.target_audience.pain_points.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="users" size="md" className="text-indigo-500" />
                    <h4 className="font-medium text-gray-900">Pain Points You Solve</h4>
                  </div>
                  <ul className="space-y-2">
                    {brandDNA.target_audience.pain_points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <Icon name="check" size="sm" className="text-green-500 mt-0.5 flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>

          {/* Industry Keywords & Themes */}
          <Card>
            <CardHeader title="Content Strategy" subtitle="Keywords and posting themes" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Industry Keywords */}
              {brandDNA.industry_keywords.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Industry Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {brandDNA.industry_keywords.map((keyword, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Posting Themes */}
              {brandDNA.posting_themes.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Content Themes</h4>
                  <div className="flex flex-wrap gap-2">
                    {brandDNA.posting_themes.map((theme, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 text-sm rounded-lg bg-indigo-50 text-indigo-700"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Unique Value */}
            {brandDNA.competitor_differentiation && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">What Makes You Unique</h4>
                <p className="text-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
                  {brandDNA.competitor_differentiation}
                </p>
              </div>
            )}
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/content-queue')}
            >
              Generate Content with DNA
            </Button>
            <Button variant="secondary" className="flex-1" onClick={() => navigate('/create')}>
              Create AI Content
            </Button>
          </div>
        </>
          )}
        </>
      )}
    </div>
  );
}
